import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback } from '@theme/utilities';

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
/** Cache product JSON by product URL for immediate variant lookups (inventory, etc.) */
const productJsonCache = new Map();

export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {string[]} Swatch value order captured before a DOM replacement, used to restore visual order after. */
  #savedSwatchOrder = [];

  /** Guard against recursive change/fetch loops when auto-selecting lower-tier options. */
  #isAutoSelecting = false;

  /**
   * Reorders color swatch labels in the given container to match the previously saved order.
   * Only operates on the non-seasonal fieldset to avoid interfering with seasonal reorganisation.
   * @param {Element} container
   */
  #reorderSwatches(container) {
    if (this.#savedSwatchOrder.length === 0) return;
    const fieldset =
      container.querySelector('fieldset.variant-option--sunice:not(.seasonal-variant-section)') ||
      container.querySelector('fieldset.variant-option--swatches:not(.seasonal-variant-section)');
    if (!fieldset) return;
    const labels = Array.from(fieldset.querySelectorAll('label.color-swatch'));
    if (labels.length < 2) return;
    const orderMap = new Map(this.#savedSwatchOrder.map((v, i) => [v, i]));
    labels
      .map((label, origIdx) => ({
        label,
        idx: orderMap.get(label.querySelector('input[type="radio"]')?.value) ?? this.#savedSwatchOrder.length + origIdx,
      }))
      .sort((a, b) => a.idx - b.idx)
      .forEach(({ label }) => fieldset.appendChild(label));
  }

  /**
   * @param {Element} container
   * @returns {HTMLInputElement | HTMLOptionElement | null | undefined}
   */
  #getCheckedOptionInContainer(container) {
    let selected = container.querySelector('input[type="radio"]:checked');
    if (!selected) {
      const sel = container.querySelector('select');
      if (sel) selected = sel.options[sel.selectedIndex];
    }
    return selected;
  }

  /**
   * @param {Element} container
   * @returns {boolean}
   */
  #containerNeedsReselect(container) {
    const selected = this.#getCheckedOptionInContainer(container);
    if (!selected) return true;
    return selected.disabled || selected.dataset?.optionAvailable === 'false';
  }

  /**
   * @param {Element} container
   * @returns {HTMLInputElement | null}
   */
  #findFirstAvailableInput(container) {
    const input = container.querySelector(
      'input[type="radio"]:not([disabled])[data-option-available="true"]'
    );
    return input instanceof HTMLInputElement ? input : null;
  }

  /**
   * @param {HTMLSelectElement} select
   * @returns {HTMLOptionElement | undefined}
   */
  #findFirstAvailableSelectOption(select) {
    return Array.from(select.options).find(
      (o) => !o.disabled && o.dataset.optionAvailable !== 'false'
    );
  }

  /**
   * Select the first available option in a single option group when the current
   * selection is missing or unavailable.
   * @param {Element} container
   * @param {Element[]} allContainers
   * @returns {boolean}
   */
  #selectFirstAvailableInContainer(container, allContainers) {
    const isColorContainer = !!container.querySelector('label.color-swatch');

    if (isColorContainer && !this.#getCheckedOptionInContainer(container)) {
      const hasCheckedColorElsewhere = allContainers.some(
        (c) => c !== container && !!c.querySelector('label.color-swatch input[type="radio"]:checked')
      );
      if (hasCheckedColorElsewhere) return false;
    }

    if (!this.#containerNeedsReselect(container)) return false;

    const firstAvailable = this.#findFirstAvailableInput(container);
    if (firstAvailable) {
      firstAvailable.checked = true;
      this.updateSelectedOption(firstAvailable);
      return true;
    }

    const sel = container.querySelector('select');
    if (sel instanceof HTMLSelectElement) {
      const availableOpt = this.#findFirstAvailableSelectOption(sel);
      if (availableOpt) {
        sel.value = availableOpt.value;
        this.updateSelectedOption(sel);
        return true;
      }
    }

    return false;
  }

  /**
   * After a higher-tier option changes, auto-select the first available value for
   * every lower-tier option group (e.g. inseam after size).
   * @param {HTMLElement} [changedOption]
   * @returns {boolean}
   */
  #autoSelectLowerTierOptions(changedOption) {
    if (this.dataset.isGiftCard === 'true') return false;

    this.#syncSizeOutOfStockVisuals();

    const optionContainers = Array.from(this.querySelectorAll('.variant-option'));
    let startIndex = 0;

    if (changedOption) {
      const changedContainer = changedOption.closest('.variant-option');
      const changedIndex = optionContainers.indexOf(changedContainer);
      if (changedIndex >= 0) startIndex = changedIndex + 1;
    }

    let didChange = false;
    for (let i = startIndex; i < optionContainers.length; i++) {
      if (this.#selectFirstAvailableInContainer(optionContainers[i], optionContainers)) {
        didChange = true;
        this.#syncSizeOutOfStockVisuals();
      }
    }

    return didChange;
  }

  /**
   * @returns {Map<number, string>}
   */
  #getSelectedOptionsByPosition() {
    const selectedByPosition = new Map();
    this.querySelectorAll('.variant-option').forEach((container) => {
      const checked = this.#getCheckedOptionInContainer(container);
      if (!checked) return;
      const pos = VariantPicker.#optionPositionFromInput(checked);
      if (pos >= 0) selectedByPosition.set(pos, checked.value);
    });
    return selectedByPosition;
  }

  /**
   * Resolve the full variant from all currently selected option values.
   * @returns {{ id: number, available: boolean, options?: string[] } | null}
   */
  #resolveSelectedVariant() {
    const productData = this.#getProductVariantsData();
    if (!productData?.variants?.length) return null;

    const selectedByPosition = this.#getSelectedOptionsByPosition();
    const optionCount = productData.options?.length ?? 0;
    if (optionCount === 0 || selectedByPosition.size < optionCount) return null;

    const norm = (v) => (v || '').toString().toLowerCase().trim();
    return (
      productData.variants.find((v) => {
        for (let i = 0; i < optionCount; i++) {
          const sel = selectedByPosition.get(i);
          if (sel == null || norm(v.options?.[i]) !== norm(sel)) return false;
        }
        return true;
      }) ?? null
    );
  }

  /**
   * The deepest-tier selected option (e.g. inseam), used as the event source id.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined}
   */
  #getLeafSelectedOption() {
    const optionContainers = Array.from(this.querySelectorAll('.variant-option'));
    for (let i = optionContainers.length - 1; i >= 0; i--) {
      const checked = this.#getCheckedOptionInContainer(optionContainers[i]);
      if (checked) return checked;
    }
    return undefined;
  }

  /**
   * Build the variant resource for VariantUpdateEvent from the live selection,
   * falling back to the embedded JSON script when ids match.
   * @param {Object | null | undefined} scriptVariant
   * @returns {Object | null}
   */
  #buildVariantResourceForEvent(scriptVariant) {
    const resolved = this.#resolveSelectedVariant();
    if (!resolved) return scriptVariant ?? null;

    if (scriptVariant && String(scriptVariant.id) === String(resolved.id)) {
      return { ...scriptVariant, available: resolved.available };
    }

    if (scriptVariant) {
      return { ...scriptVariant, id: resolved.id, available: resolved.available };
    }

    return { id: resolved.id, available: resolved.available };
  }

  /**
   * Dispatch variant events after a programmatic selection change (no section re-fetch).
   */
  #dispatchVariantEventsFromSelection() {
    const leafOption = this.#getLeafSelectedOption();
    if (!leafOption) return;

    const resolved = this.#resolveSelectedVariant();
    const variantId = resolved ? String(resolved.id) : leafOption.dataset.variantId || null;

    this.dispatchEvent(
      new VariantSelectedEvent({
        id: leafOption.dataset.optionValueId ?? '',
        variantId,
        featuredMediaId:
          this.querySelector('label.color-swatch input[type="radio"]:checked')?.dataset.featuredMediaId || null,
      })
    );

    const textContent = this.querySelector('script[type="application/json"]')?.textContent;
    const scriptVariant = textContent ? JSON.parse(textContent) : null;
    const resource = this.#buildVariantResourceForEvent(scriptVariant);

    if (resource && leafOption.dataset.optionValueId) {
      this.dispatchEvent(
        new VariantUpdateEvent(resource, leafOption.dataset.optionValueId, {
          html: null,
          productId: this.dataset.productId ?? '',
          newProduct: undefined,
        })
      );
    }
  }

  /**
   * Option position from Liquid `data-input-id` (format: "{position}-{index}").
   * @param {HTMLInputElement | HTMLOptionElement | null | undefined} input
   * @returns {number}
   */
  static #optionPositionFromInput(input) {
    const inputId = input?.dataset?.inputId;
    if (!inputId) return -1;
    const pos = parseInt(inputId.split('-')[0], 10);
    return Number.isNaN(pos) ? -1 : pos - 1;
  }

  /**
   * Ensures every non-colour option label's availability state is correct.
   * Uses product-variants-data JSON and the standard Shopify availability matrix:
   * option value N is available when ANY variant matches all currently selected
   * options at positions other than N plus this value, and is purchasable.
   * @param {Element} [root] - Element to query within. Defaults to this.
   */
  #syncSizeOutOfStockVisuals(root) {
    const el = root || this;
    const OOS_CLASS = 'variant-option__button-label--out-of-stock';

    const script = el.querySelector('.product-variants-data');
    let productData = null;
    if (script instanceof HTMLScriptElement) {
      try {
        productData = JSON.parse(script.textContent || '{}');
      } catch {
        /* ignore */
      }
    }

    const variants = productData?.variants || [];
    const optionsMeta = productData?.options || [];
    const optionCount = optionsMeta.length;
    const norm = (v) => (v || '').toString().toLowerCase().trim();

    /** 0-based positions for Size / Inseam in 3-option (Color → Size → Inseam) products. */
    const sizeTierPos =
      optionCount === 3
        ? (optionsMeta.find((o) => /^size$/i.test(o?.name || ''))?.position ?? 2) - 1
        : -1;
    const inseamTierPos =
      optionCount === 3
        ? (optionsMeta.find((o) => /^inseam$/i.test(o?.name || ''))?.position ?? 3) - 1
        : -1;

    const selectedByPosition = new Map();
    el.querySelectorAll('.variant-option').forEach((container) => {
      const checked = container.querySelector('input[type="radio"]:checked');
      if (!checked) return;
      const pos = VariantPicker.#optionPositionFromInput(checked);
      if (pos >= 0) selectedByPosition.set(pos, norm(checked.value));
    });

    /**
     * 3-option products: sizes ignore the selected inseam (available when ANY inseam works);
     * inseams use color + size; color keeps the standard matrix. 1–2 option products unchanged.
     * @param {number} thisPos
     * @param {number} selectedPos
     * @returns {boolean}
     */
    const shouldMatchSelection = (thisPos, selectedPos) => {
      if (selectedPos === thisPos) return false;
      if (optionCount === 3 && sizeTierPos >= 0 && thisPos === sizeTierPos) {
        return selectedPos < sizeTierPos;
      }
      if (optionCount === 3 && inseamTierPos >= 0 && thisPos === inseamTierPos) {
        return selectedPos < inseamTierPos;
      }
      return true;
    };

    const isValueAvailable = (thisPos, inputValue) =>
      variants.some((v) => {
        for (const [pos, val] of selectedByPosition) {
          if (!shouldMatchSelection(thisPos, pos)) continue;
          if (norm(v.options?.[pos]) !== val) return false;
        }
        return norm(v.options?.[thisPos]) === inputValue && v.available === true;
      });

    const applyAvailability = (input, label, isUnavailable) => {
      const isAvailable = !isUnavailable;
      input.dataset.optionAvailable = isAvailable ? 'true' : 'false';
      if (label) label.dataset.optionAvailable = isAvailable ? 'true' : 'false';
      input.disabled = isUnavailable;
      if (isUnavailable) {
        input.setAttribute('aria-disabled', 'true');
      } else {
        input.removeAttribute('aria-disabled');
      }
      if (label) label.classList.toggle(OOS_CLASS, isUnavailable);
    };

    el.querySelectorAll('.variant-option').forEach((container) => {
      if (container.querySelector('label.color-swatch')) return;

      container
        .querySelectorAll(
          'label.variant-option__button-label, label.sunice-variant-option__button-label:not(.color-swatch)'
        )
        .forEach((label) => {
          if (label.classList.contains('color-swatch')) return;
          const input = label.querySelector('input[type="radio"]');
          if (!input) return;

          const inputValue = norm(input.value);
          const thisPos = VariantPicker.#optionPositionFromInput(input);

          let isUnavailable;
          if (thisPos >= 0 && selectedByPosition.size > 0) {
            isUnavailable = !isValueAvailable(thisPos, inputValue);
          } else {
            isUnavailable = input.dataset.optionAvailable === 'false' || input.disabled;
          }

          applyAvailability(input, label, isUnavailable);
        });

      const select = container.querySelector('select.variant-option__select');
      if (select) {
        Array.from(select.options).forEach((option) => {
          const inputValue = norm(option.value);
          const thisPos = VariantPicker.#optionPositionFromInput(option);
          if (thisPos < 0 || selectedByPosition.size === 0) return;
          const isUnavailable = !isValueAvailable(thisPos, inputValue);
          option.disabled = isUnavailable;
          option.dataset.optionAvailable = isUnavailable ? 'false' : 'true';
        });
      }
    });
  }


  /**
   * Fetches product JSON and returns variant by ID. Uses cache for instant repeat lookups.
   * Enables immediate inventory/price updates when color swatch is clicked (before section fetch).
   * @param {string} productUrl - e.g. /products/foo
   * @param {string|number} variantId - Variant ID
   * @returns {Promise<Object|null>} Variant object or null
   */
  static async getProductJson(productUrl) {
    if (!productUrl) return null;
    const baseUrl = (productUrl || '').split('?')[0].replace(/\/$/, '');
    const jsonUrl = baseUrl ? `${baseUrl}.js` : '';
    if (!jsonUrl) return null;
    let product = productJsonCache.get(jsonUrl);
    if (!product) {
      try {
        const res = await fetch(jsonUrl);
        if (!res.ok) return null;
        product = await res.json();
        productJsonCache.set(jsonUrl, product);
      } catch {
        return null;
      }
    }
    return product;
  }

  static async getVariantFromProductJson(productUrl, variantId) {
    const product = await VariantPicker.getProductJson(productUrl);
    if (!product?.variants) return null;
    return product.variants.find((v) => String(v.id) === String(variantId)) || null;
  }

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('change', this.variantChanged.bind(this));
    
    // Add click event listener for radio buttons to ensure active class is applied
    this.addEventListener('click', (event) => {
      if (event.target.type === 'radio') {
        this.updateSelectedOption(event.target);
      }
    });

    // Ensure first available (non-disabled) option is selected when a disabled one is currently selected
    requestAnimationFrame(() => {
      this.#syncSizeOutOfStockVisuals();
      const didChange = this.ensureFirstAvailableSelected();
      if (didChange) {
        this.#dispatchVariantEventsFromSelection();
      }
    });

    // Preload product JSON for instant inventory/price updates when color swatch is clicked
    const productUrl = this.dataset.productUrl;
    if (productUrl && /\/products\//.test(productUrl)) {
      const baseUrl = productUrl.split('?')[0].replace(/\/$/, '');
      if (baseUrl && !productJsonCache.has(baseUrl + '.js')) {
        fetch(baseUrl + '.js')
          .then((r) => (r.ok ? r.json() : null))
          .then((product) => {
            if (product) productJsonCache.set(baseUrl + '.js', product);
          })
          .catch(() => {});
      }
    }

    // Preload variant featured image on color swatch hover to speed up gallery update on click
    this.#setupColorSwatchImagePreload();
  }

  /**
   * Preloads variant featured images when hovering over color swatches.
   * Improves perceived load time when user clicks a swatch.
   */
  #setupColorSwatchImagePreload() {
    const productUrl = this.dataset.productUrl;
    if (!productUrl || !/\/products\//.test(productUrl)) return;

    this.addEventListener(
      'pointerover',
      (e) => {
        const target = e.target?.closest?.('label.color-swatch, label.sunice-variant-option__button-label.color-swatch, label.variant-option__button-label--has-swatch');
        if (!target) return;
        const input = target.querySelector?.('input[data-variant-id]');
        const variantId = input?.dataset?.variantId;
        if (!variantId) return;

        VariantPicker.getProductJson(productUrl).then((product) => {
          if (!product?.variants) return;
          const variant = product.variants.find((v) => String(v.id) === String(variantId));
          const featuredMedia = variant?.featured_media;
          const imgSrc = featuredMedia?.preview_image?.src || featuredMedia?.src;
          if (imgSrc) {
            const img = new Image();
            img.src = imgSrc;
          }
        });
      },
      { capture: true, passive: true }
    );
  }

  /**
   * If the selected option in any group is disabled but an available option exists,
   * select the first available option for each affected group.
   * @returns {boolean} Whether any selection changed
   */
  ensureFirstAvailableSelected() {
    if (this.dataset.isGiftCard === 'true') return false;

    this.#syncSizeOutOfStockVisuals();

    const optionContainers = Array.from(this.querySelectorAll('.variant-option'));
    let didChange = false;

    for (const container of optionContainers) {
      if (this.#selectFirstAvailableInContainer(container, optionContainers)) {
        didChange = true;
        this.#syncSizeOutOfStockVisuals();
      }
    }

    return didChange;
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (this.#isAutoSelecting) return;
    
    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    const isLinkedGroup = selectedOption.dataset.linkedGroup === 'true';

    this.updateSelectedOption(event.target);

    // For linked-group (metafield) swatches, skip VariantSelectedEvent — the gallery
    // can't resolve a sibling product's variantId against the current product's media,
    // causing it to fall back to the native variant image. The updateMain morph and the
    // subsequent VariantUpdateEvent will update the gallery correctly instead.
    if (!isLinkedGroup) {
      this.dispatchEvent(
        new VariantSelectedEvent({
          id: selectedOption.dataset.optionValueId ?? '',
          variantId: selectedOption.dataset.variantId || null,
          featuredMediaId: selectedOption.dataset.featuredMediaId || null,
        })
      );
    }

    const variantId = selectedOption.dataset.variantId || null;

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    // For same-product changes, immediately recompute availability and auto-select
    // the first available lower-tier option (e.g. inseam after size change).
    // Skip for cross-product (metafield) swatches — the morph replaces everything.
    if (!loadsNewProduct) {
      this.#syncSizeOutOfStockVisuals();
      this.#autoSelectLowerTierOptions(selectedOption);
      this.#syncSizeOutOfStockVisuals();
    }

    const requestUrl = this.buildRequestUrl(selectedOption, loadsNewProduct);
    const linkedSelection =
      loadsNewProduct && isLinkedGroup
        ? {
            value: selectedOption.value || '',
            variantId: selectedOption.dataset.variantId || '',
            featuredMediaId: selectedOption.dataset.featuredMediaId || '',
          }
        : undefined;
    this.fetchUpdatedSection(requestUrl, loadsNewProduct, linkedSelection);

    const url = new URL(window.location.href);
    const resolvedVariant = this.#resolveSelectedVariant();

    if (isOnProductPage && !isLinkedGroup) {
      const resolvedVariantId = resolvedVariant ? String(resolvedVariant.id) : variantId;
      if (resolvedVariantId) {
        url.searchParams.set('variant', resolvedVariantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // For combined listings, change the URL path. For linked-group swatches, keep the current URL.
    if (loadsNewProduct && !isLinkedGroup) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    // Check if this is a gift card product
    const isGiftCard = this.dataset.isGiftCard === 'true';

    if (target instanceof HTMLInputElement) {
      target.checked = true;
      
      // Add active class to the label for Sunice variant pickers
      const label = target.closest('label');
      if (label) {
        const fieldset = target.closest('fieldset');
        // For color swatches (Core or Seasonal), clear active from ALL color swatches in picker
        // so that when switching between Core and Seasonal, the other section loses active state
        if (label.classList.contains('color-swatch')) {
          this.querySelectorAll('label.color-swatch').forEach((l) => {
            l.classList.remove('active');
            l.removeAttribute('data-active');
          });
        } else if (fieldset) {
          const allLabels = fieldset.querySelectorAll('label');
          allLabels.forEach(l => {
            l.classList.remove('active');
            l.removeAttribute('data-active');
          });
        }
        
        // Add active class to the clicked label
        label.classList.add('active');
        
        // For color swatches, also set data-active attribute
        if (label.classList.contains('color-swatch')) {
          label.setAttribute('data-active', 'true');
        }
        
        // For gift cards, ensure the clicked variant is always marked as available
        if (isGiftCard) {
          label.setAttribute('data-option-available', 'true');
          target.setAttribute('data-option-available', 'true');
          // Remove any unavailable classes
          label.classList.remove('unavailable');
          label.removeAttribute('data-unavailable');
          
          // Update the price in the legend
          const fieldset = target.closest('fieldset');
          if (fieldset) {
            const legend = fieldset.querySelector('legend');
            if (legend) {
              const variantPrice = target.dataset.variantPrice;
              if (variantPrice) {
                // Find or create the selected value span
                let priceSpan = legend.querySelector('.variant-option__selected-value');
                if (!priceSpan) {
                  priceSpan = document.createElement('span');
                  priceSpan.className = 'variant-option__selected-value';
                  legend.appendChild(priceSpan);
                }
                priceSpan.textContent = variantPrice;
              }
            }
          }
        }
      }
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Gets selected option value IDs for the request.
   * When color changes, includes size too if the current size is still available for the new color
   * (so the server preserves the size selection instead of resetting to first available).
   * @param {HTMLElement} changedOption - The option element that was changed (radio input or select option).
   * @returns {string[]} Option value IDs.
   */
  getSelectedOptionsValuesUpToChanged(changedOption) {
    const optionContainers = Array.from(this.querySelectorAll('.variant-option'));
    const changedContainer = changedOption.closest('.variant-option');
    if (!changedContainer) return this.selectedOptionsValues;
    const changedIndex = optionContainers.indexOf(changedContainer);
    if (changedIndex < 0) return this.selectedOptionsValues;

    const values = [];
    for (let i = 0; i <= changedIndex && i < optionContainers.length; i++) {
      const container = optionContainers[i];
      let checked = container.querySelector('input[type="radio"]:checked');
      if (!checked) {
        const sel = container.querySelector('select');
        if (sel) checked = sel.options[sel.selectedIndex];
      }
      if (checked && checked.dataset?.optionValueId) values.push(checked.dataset.optionValueId);
    }

    // When a color option changes (not size), include size if it's still available for the new color
    const changedLegend = changedContainer?.querySelector('legend');
    const isColorChange =
      changedLegend &&
      /Color|Colour|Core|Current|Seasonal/i.test(changedLegend.textContent || '');
    const sizeContainer = optionContainers.find((c) => {
      const leg = c.querySelector('legend');
      return leg && /^Size:/i.test((leg.textContent || '').trim());
    });
    if (isColorChange && sizeContainer) {
      const sizeChecked = sizeContainer.querySelector('input[type="radio"]:checked');
      const newColorValue =
        (changedOption instanceof HTMLInputElement || changedOption instanceof HTMLOptionElement)
          ? changedOption.value
          : (changedOption.textContent || '').trim();
      const currentSizeValue =
        sizeChecked instanceof HTMLInputElement ? sizeChecked.value : null;
      if (newColorValue && currentSizeValue) {
        const productData = this.#getProductVariantsData();
        if (productData && this.#isSizeAvailableForColor(productData, newColorValue, currentSizeValue)) {
          const sizeOptionValueId =
            sizeChecked instanceof HTMLInputElement ? sizeChecked.dataset?.optionValueId : null;
          if (sizeOptionValueId && !values.includes(sizeOptionValueId)) {
            values.push(sizeOptionValueId);
          }
        }
      }
    }

    return values.length > 0 ? values : this.selectedOptionsValues;
  }

  /**
   * Gets product variants data from the product-variants-data script (PDP only).
   * @returns {{ variants: Array, options: Array } | null}
   */
  #getProductVariantsData() {
    const script = this.querySelector('.product-variants-data');
    if (!(script instanceof HTMLScriptElement)) return null;
    try {
      return JSON.parse(script.textContent || '{}');
    } catch {
      return null;
    }
  }

  /**
   * Checks if the given size is available for the given color.
   * @param {{ variants: Array, options: Array }} productData
   * @param {string} colorValue - Color option value (e.g. "Black")
   * @param {string} sizeValue - Size option value (e.g. "M")
   * @returns {boolean}
   */
  #isSizeAvailableForColor(productData, colorValue, sizeValue) {
    const variants = productData?.variants || [];
    const options = productData?.options || [];
    const colorOpt = options.find((o) => o?.name === 'Color' || o?.name === 'Colour');
    const sizeOpt = options.find((o) => o?.name === 'Size');
    if (!colorOpt || !sizeOpt) return false;
    const colorPos = colorOpt.position - 1;
    const sizePos = sizeOpt.position - 1;
    const norm = (v) => (v || '').toString().toLowerCase().trim();
    const colorNorm = norm(colorValue);
    const sizeNorm = norm(sizeValue);
    return variants.some(
      (v) =>
        norm(v.options?.[colorPos]) === colorNorm &&
        norm(v.options?.[sizePos]) === sizeNorm &&
        v.available === true
    );
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {boolean} [loadsNewProduct] - If loading a new product (combined listing).
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, loadsNewProduct = false, source = null, sourceSelectedOptionsValues = []) {
    // For same-product changes, always use the element's product URL.
    // #pendingRequestUrl can be stale from an aborted cross-product fetch
    // (e.g. clicking a metafield colour then quickly clicking a PDP colour).
    if (!loadsNewProduct) {
      this.#pendingRequestUrl = undefined;
    }
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;

    // Linked-group swatch: fetch the sibling product page with the specific variant
    if (loadsNewProduct && selectedOption.dataset.linkedGroup === 'true') {
      const vid = selectedOption.dataset.variantId;
      return vid ? `${productUrl}?variant=${vid}` : productUrl;
    }

    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      const isOnProductPage =
        this.dataset.templateProductMatch === 'true' &&
        !this.closest('product-card') &&
        !this.closest('quick-add-dialog');
      const optionsToSend =
        isOnProductPage && !loadsNewProduct
          ? this.selectedOptionsValues
          : this.getSelectedOptionsValuesUpToChanged(selectedOption);
      params.push(`option_values=${optionsToSend.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }

    // On PDP, fetch only the main section to reduce payload and speed up media gallery update (unless loading a different product)
    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !this.closest('product-card') &&
      !this.closest('quick-add-dialog');
    if (isOnProductPage && !loadsNewProduct && params.length > 0) {
      const sectionId = this.dataset.sectionId || 'main';
      params.push(`section_id=${sectionId}`);
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   * @param {{ value: string, variantId: string, featuredMediaId: string } | undefined} linkedSelection
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false, linkedSelection) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        if (shouldMorphMain) {
          this.#savedSwatchOrder = [];
          this.querySelectorAll('label.color-swatch input[type="radio"]').forEach(
            (input) => this.#savedSwatchOrder.push(input.value)
          );

          // Deep-clone elements from the response BEFORE the morph runs.
          // morph() uses appendChild to move unmatched children from the source tree
          // into the live DOM, which mutates the parsed HTML document. If we read
          // elements AFTER the morph, their innerHTML may be incomplete (missing
          // children that were moved out).
          const freshPicker = html.querySelector('variant-picker')?.cloneNode(true);
          const freshGallery = html.querySelector('media-gallery')?.cloneNode(true);

          // The selected linked colour is carried explicitly through the request.
          // Do not rely on radio-group parsing or the sibling product's default
          // variant when the response is installed: both can briefly disagree on
          // mobile while the overflow swatch row is being rebuilt.
          let requestedColor;
          if (freshPicker instanceof HTMLElement && linkedSelection) {
            const colorInputs = Array.from(
              freshPicker.querySelectorAll('label.color-swatch input[type="radio"]')
            );
            requestedColor = colorInputs.find(
              (input) =>
                (linkedSelection.variantId && input.dataset.variantId === linkedSelection.variantId) ||
                (linkedSelection.value && input.value === linkedSelection.value)
            );

            if (requestedColor instanceof HTMLInputElement) {
              colorInputs.forEach((input) => {
                input.checked = input === requestedColor;
                if (input === requestedColor) input.setAttribute('checked', 'checked');
                else input.removeAttribute('checked');
              });

              freshPicker.querySelectorAll('label.color-swatch').forEach((label) => {
                const isRequested = label.contains(requestedColor);
                label.classList.toggle('active', isRequested);
                if (isRequested) label.setAttribute('data-active', 'true');
                else label.removeAttribute('data-active');
              });
            }
          }

          // A linked-product swatch replaces the entire <main>. Previously that
          // morph changed the existing gallery once, then the clean gallery clone
          // below replaced it a second time. The intermediate gallery retained the
          // old slideshow index, which could briefly show angle two before the
          // assigned featured image appeared. Give morph() a snapshot of the live
          // gallery instead so it leaves that subtree visually untouched; then do
          // the sibling gallery update once, atomically, below.
          const livePickerBeforeMorph =
            document.querySelector('main variant-picker[data-template-product-match="true"]') || this;
          const responsePicker = html.querySelector(
            'main variant-picker[data-template-product-match="true"], main variant-picker'
          );
          if (livePickerBeforeMorph instanceof HTMLElement && responsePicker instanceof HTMLElement) {
            responsePicker.replaceWith(livePickerBeforeMorph.cloneNode(true));
          }

          const liveGalleryBeforeMorph = document.querySelector('main media-gallery');
          const responseGallery = html.querySelector('main media-gallery');
          if (liveGalleryBeforeMorph instanceof HTMLElement && responseGallery instanceof HTMLElement) {
            responseGallery.replaceWith(liveGalleryBeforeMorph.cloneNode(true));
          }

          this.updateMain(html);

          if (freshPicker instanceof HTMLElement) {
            this.#reorderSwatches(freshPicker);

            const livePicker =
              livePickerBeforeMorph instanceof HTMLElement && livePickerBeforeMorph.isConnected
                ? livePickerBeforeMorph
                : document.querySelector('main variant-picker[data-template-product-match="true"]');
            if (!(livePicker instanceof VariantPicker)) return;

            for (const attr of Array.from(livePicker.attributes)) {
              if (!freshPicker.hasAttribute(attr.name)) livePicker.removeAttribute(attr.name);
            }
            for (const attr of Array.from(freshPicker.attributes)) {
              livePicker.setAttribute(attr.name, attr.value);
            }
            livePicker.innerHTML = freshPicker.innerHTML;

            // innerHTML sets checked ATTRIBUTE; sync the PROPERTY so JS
            // reads the correct selection on subsequent size clicks.
            livePicker.querySelectorAll('input[type="radio"][checked]').forEach((radio) => {
              radio.checked = true;
            });

            livePicker.querySelectorAll('label.color-swatch').forEach((l) => {
              l.classList.remove('active');
              l.removeAttribute('data-active');
            });
            const correctColor =
              (linkedSelection
                ? Array.from(livePicker.querySelectorAll('label.color-swatch input[type="radio"]')).find(
                    (input) =>
                      (linkedSelection.variantId && input.dataset.variantId === linkedSelection.variantId) ||
                      (linkedSelection.value && input.value === linkedSelection.value)
                  )
                : null) || livePicker.querySelector('label.color-swatch input[type="radio"]:checked');
            if (correctColor) {
              correctColor.checked = true;
              const lbl = correctColor.closest('label');
              if (lbl) {
                lbl.classList.add('active');
                lbl.setAttribute('data-active', 'true');
              }
            }

            livePicker.ensureFirstAvailableSelected();

            // Replace the media gallery with the pre-morph clone so the sibling
            // product's images, thumbnails, and zoom-dialog load cleanly.
            // The clone was taken before morph() so all child refs (dialog, media,
            // thumbnails) are intact — avoiding the MissingRefError that occurs when
            // morph() mutates the parsed HTML and leaves the gallery incomplete.
            // The target variant is attached to the clone before insertion so
            // media-gallery can select it during connectedCallback.
            if (freshGallery instanceof HTMLElement) {
              if (linkedSelection?.variantId) {
                freshGallery.dataset.initialVariantId = linkedSelection.variantId;
              }
              if (linkedSelection?.featuredMediaId) {
                freshGallery.dataset.initialFeaturedMediaId = linkedSelection.featuredMediaId;
              }

              // Always resolve the live gallery after the protected main morph.
              // Mobile grid/carousel layouts can cause morph() to reconnect the
              // original node even when its markup was cloned for protection.
              const liveGallery = document.querySelector('main media-gallery');
              if (liveGallery) {
                try {
                  liveGallery.replaceWith(freshGallery);
                } catch {
                  // Swallow zoom-dialog disconnectedCallback errors from the morphed gallery
                }
              }
            }

            if (correctColor) {
              livePicker.dispatchEvent(
                new VariantSelectedEvent({
                  id: correctColor.dataset.optionValueId ?? '',
                  variantId: correctColor.dataset.variantId || null,
                  featuredMediaId: correctColor.dataset.featuredMediaId || null,
                })
              );
            }

            const textContent = freshPicker.querySelector('script[type="application/json"]')?.textContent;
            if (textContent && correctColor?.dataset.optionValueId) {
              livePicker.dispatchEvent(
                new VariantUpdateEvent(JSON.parse(textContent), correctColor.dataset.optionValueId, {
                  html: null,
                  productId: livePicker.dataset.productId ?? '',
                  newProduct: undefined,
                })
              );
            }
          }
          return;
        }

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        const newProduct = this.updateVariantPicker(html);

        // Re-sync selection after morph, then emit events with the resolved full variant
        // (embedded JSON may still describe an OOS combo when inseam was auto-corrected).
        this.#syncSizeOutOfStockVisuals();
        this.ensureFirstAvailableSelected();

        const leafOption = this.#getLeafSelectedOption();
        const sourceId = leafOption?.dataset?.optionValueId ?? this.selectedOptionId;
        const resource = this.#buildVariantResourceForEvent(JSON.parse(textContent));

        if (sourceId && resource) {
          this.dispatchEvent(
            new VariantUpdateEvent(resource, sourceId, {
              html,
              productId: this.dataset.productId ?? '',
              newProduct,
            })
          );
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // Preserve color swatch backgrounds (file-based images) before morph so they are not lost
    // when only size changes - server may re-render with grey/product image; we keep the originals
    const swatchBackgroundsByValue = new Map();
    this.querySelectorAll('.color-swatch .swatch, .sunice-variant-option__button-label.color-swatch .swatch').forEach((swatchEl) => {
      const label = swatchEl.closest('label');
      const input = label?.querySelector('input[type="radio"]');
      if (input instanceof HTMLInputElement && label) {
        const value = input.value || input.dataset.optionValueId || '';
        const bg = swatchEl.style.getPropertyValue('--swatch-background')?.trim() ||
          swatchEl.dataset.swatchBackground ||
          getComputedStyle(swatchEl).getPropertyValue('--swatch-background')?.trim();
        if (value && bg && bg !== 'rgb(200, 200, 200)' && bg !== '') {
          swatchBackgroundsByValue.set(value, bg);
        }
      }
    });

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }
    }

    // Save currently checked color BEFORE replacing content so we can restore it after.
    // The seasonal fieldset reorganization and ensureFirstAvailableSelected can otherwise
    // inadvertently change the color selection when only a size was clicked.
    const prevCheckedColor = this.querySelector('label.color-swatch input[type="radio"]:checked');
    const savedColorValue = prevCheckedColor?.value;

    // Replace content directly instead of morphing — the seasonal fieldset
    // reorganization alters the DOM in ways that cause morph to mismatch labels,
    // leading to wrong color selection and reordering. A full replacement lets the
    // server's HTML be the source of truth (correct checked state, correct order).
    this.#reorderSwatches(newVariantPickerSource);

    for (const attr of Array.from(newVariantPickerSource.attributes)) {
      this.setAttribute(attr.name, attr.value);
    }
    this.innerHTML = newVariantPickerSource.innerHTML;

    // Force the previously checked color to remain checked. After innerHTML the
    // browser may pick a different radio if the DOM order changed. Only force when
    // the saved color exists, is still in the DOM, and is available.
    if (savedColorValue) {
      const restoredRadio = Array.from(
        this.querySelectorAll('label.color-swatch input[type="radio"]')
      ).find((r) => r.value === savedColorValue);

      if (restoredRadio && !restoredRadio.disabled && restoredRadio.dataset.optionAvailable !== 'false') {
        restoredRadio.checked = true;
        this.querySelectorAll('label.color-swatch').forEach((l) => {
          l.classList.remove('active');
          l.removeAttribute('data-active');
        });
        const restoredLabel = restoredRadio.closest('label');
        if (restoredLabel) {
          restoredLabel.classList.add('active');
          restoredLabel.setAttribute('data-active', 'true');
        }
      }
    }

    // Restore color swatch backgrounds so file-based images stay (not grey/product image)
    if (swatchBackgroundsByValue.size > 0) {
      this.querySelectorAll('.color-swatch .swatch, .sunice-variant-option__button-label.color-swatch .swatch').forEach((swatchEl) => {
        const label = swatchEl.closest('label');
        const input = label?.querySelector('input[type="radio"]');
        if (input instanceof HTMLInputElement) {
          const value = input.value || input.dataset.optionValueId || '';
          const savedBg = swatchBackgroundsByValue.get(value);
          if (savedBg) {
            swatchEl.style.setProperty('--swatch-background', savedBg);
            swatchEl.dataset.swatchBackground = savedBg;
          }
        }
      });
    }

    // Check if this is a gift card product
    const isGiftCard = this.dataset.isGiftCard === 'true';

    // CRITICAL: Do NOT modify data-option-available based on background color
    // The Liquid template already sets the correct data-option-available attributes
    // based on actual variant availability. We should trust those values.
    // Only handle gift cards which need special treatment
    if (isGiftCard) {
      // For gift cards, ensure all variants are marked as available
      this.querySelectorAll('input[type="radio"]').forEach((input) => {
        // Skip facet swatches - never modify them (desktop or mobile drawer)
        if (input.closest('.facets__item, .facets__panel, .facets--drawer, facets-form-component')) {
          return;
        }
        
        const label = input.closest('label');
        if (label && input) {
          label.setAttribute('data-option-available', 'true');
          input.setAttribute('data-option-available', 'true');
        }
      });
      
      // Update the price in the legend for gift cards
      this.querySelectorAll('input[type="radio"]:checked').forEach((checkedInput) => {
        const fieldset = checkedInput.closest('fieldset');
        if (fieldset) {
          const legend = fieldset.querySelector('legend');
          if (legend) {
            const variantPrice = checkedInput.dataset.variantPrice;
            if (variantPrice) {
              // Find or create the selected value span
              let priceSpan = legend.querySelector('.variant-option__selected-value');
              if (!priceSpan) {
                priceSpan = document.createElement('span');
                priceSpan.className = 'variant-option__selected-value';
                legend.appendChild(priceSpan);
              }
              priceSpan.textContent = variantPrice;
            }
          }
        }
      });
    }
    
    // Recompute size availability from the product-variants-data JSON.
    this.#syncSizeOutOfStockVisuals();

    // Safety net: if server HTML still checked an OOS lower-tier value, fix locally.
    // Events are dispatched by fetchUpdatedSection after this returns.
    this.ensureFirstAvailableSelected();

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
